import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Tabs whose choice lives in the URL (`?tab=`, through `useTab`), so a tab can
 * be linked to, the back button returns to it, and a reload keeps it.
 *
 * The WAI-ARIA tabs pattern: one tab in the tab order at a time, arrow keys move
 * between them, Home and End jump to the ends. The panel is labelled by its tab.
 */

export interface TabItem<Id extends string> {
  id: Id;
  label: string;
  /** A small count after the label, when it says something (open events, say). */
  count?: number | undefined;
}

export function Tabs<Id extends string>({
  label,
  tabs,
  current,
  onSelect,
  idPrefix,
}: {
  /** What the tabs are of, for a screen reader. */
  label: string;
  tabs: readonly TabItem<Id>[];
  current: Id;
  onSelect: (id: Id) => void;
  idPrefix: string;
}): ReactNode {
  const refs = useRef(new Map<Id, HTMLButtonElement>());

  const move = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const index = tabs.findIndex((tab) => tab.id === current);
    const target =
      event.key === 'ArrowRight'
        ? tabs[(index + 1) % tabs.length]
        : event.key === 'ArrowLeft'
          ? tabs[(index - 1 + tabs.length) % tabs.length]
          : event.key === 'Home'
            ? tabs[0]
            : event.key === 'End'
              ? tabs[tabs.length - 1]
              : undefined;
    if (target === undefined) return;
    event.preventDefault();
    onSelect(target.id);
    refs.current.get(target.id)?.focus();
  };

  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => {
        const selected = tab.id === current;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node === null) refs.current.delete(tab.id);
              else refs.current.set(tab.id, node);
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel`}
            tabIndex={selected ? 0 : -1}
            className="tab"
            onClick={() => {
              onSelect(tab.id);
            }}
            onKeyDown={move}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="tab-count">{tab.count.toLocaleString('en-US')}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  current,
  children,
}: {
  idPrefix: string;
  current: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel`}
      aria-labelledby={`${idPrefix}-tab-${current}`}
      className="tab-panel"
    >
      {children}
    </div>
  );
}
